import { getAuthHeaders, handleAuthError } from "../../../../utils/authUtils";
import { API_BASE_URL } from "../../../../environment";
import { requestExportByQuery } from "../../Dashboard/dashboardApi";
import { ChatService } from "../../../application/components/chat/chatService";
import { isInternalPlatform } from "./chatAnalyticsPalette";

export interface ConnectedBot {
  external_chat_tenant_bot_id: number;
  external_chat_platform_id?: number;
  bot_name: string;
  bot_type: string;
  chat_type_key: string;
}

export interface ChatScoreCards {
  total_chats: {
    external: number;
    internal: number;
  };
  total_messages: {
    external: number;
    internal: number;
  };
  total_connected_bots: {
    external: number;
    internal: number;
  };
  total_chat_groups: {
    external: number;
    internal: number;
  };
  external_chat_message_evaluation: {
    total_agent_replies: number;
    total_customer_messages: number;
    total_bot_replies: number;
  };
  internal_chat_message_evaluation: {
    total_sent_messages: number;
    total_received_messages: number;
  };
}

export interface ChannelCount {
  external_chat_channel_id?: number | null;
  external_chat_platform_id?: number | null;
  chat_type_key?: string | null;
  external_chat_channel: string;
  total_chats: number;
  total_messages: number;
  total_bot_replies: number;
  total_agent_replies: number;
}

export interface AgentCount {
  user_id?: number;
  agent_name: string;
  total_chats: number;
  total_messages: number;
  total_agent_replies: number;
}

export interface AssignedTeamCount {
  team_id?: number;
  assigned_team_name: string;
  total_chats: number;
  total_messages: number;
  total_bot_replies: number;
  total_agent_replies: number;
}

export interface TabularExternalChatRow {
  external_chat_enduser_name: string;
  total_messages: number;
  total_agent_messages: number;
  total_enduser_messages: number;
  total_bot_messages: number;
}

export interface TabularInternalChatRow {
  external_chat_enduser_name: string;
  total_messages: number;
  total_sent_messages: number;
  total_received_messages: number;
}

export interface TabularView {
  external_chat: TabularExternalChatRow[];
  internal_chat: TabularInternalChatRow[];
}

export interface ChatPlatform {
  external_chat_platform_id: number;
  external_chat_platform_key: string;
  external_chat_platform_display_name: string;
}

export interface ChatAnalyticsDashboardResponse {
  connected_bots: number;
  connected_bot_list: ConnectedBot[];
  external_chat_platforms: ChatPlatform[];
  score_cards: ChatScoreCards;
  chat_counts_by_channels: ChannelCount[];
  chat_counts_by_agents: AgentCount[];
  chat_counts_by_assigned_teams: AssignedTeamCount[];
  tabular_view: TabularView;
}

export interface ChatAnalyticsFilters {
  startDatetime?: string | null;
  endDatetime?: string | null;
  salesUserId?: number | string | null;
  assignedTeamId?: number | string | null;
  externalChatEndUserStatusId?: number | null;
  chatTypeId?: number | null;
  externalChatTenantBotId?: number | string | null;
  externalChatChannelId?: number | string | null;
  externalChatPlatformId?: number | string | null;
}

export interface FilterOption {
  value: number | string;
  label: string;
}

export interface BotFilterOption extends FilterOption {
  isInternal: boolean;
}

const raiseForStatus = async (response: Response, fallbackMessage: string) => {
  if (response.status === 401 || response.status === 403) {
    handleAuthError({ response: { status: response.status } });
  }
  throw new Error(`${fallbackMessage} (${response.status})`);
};

const buildDashboardQuery = (
  tenantId: string | number,
  filters: ChatAnalyticsFilters
) => {
  const params = new URLSearchParams();
  params.append("tenant_id", String(tenantId));
  if (filters.startDatetime) params.append("start_datetime", filters.startDatetime);
  if (filters.endDatetime) params.append("end_datetime", filters.endDatetime);
  if (filters.salesUserId) params.append("sales_user_id", String(filters.salesUserId));
  if (filters.assignedTeamId) params.append("assigned_team_id", String(filters.assignedTeamId));
  if (filters.externalChatEndUserStatusId)
    params.append(
      "external_chat_end_user_status_id",
      String(filters.externalChatEndUserStatusId)
    );
  if (filters.chatTypeId) params.append("chat_type_id", String(filters.chatTypeId));
  if (filters.externalChatTenantBotId)
    params.append("external_chat_tenant_bot_id", String(filters.externalChatTenantBotId));
  if (filters.externalChatChannelId)
    params.append("external_chat_channel_id", String(filters.externalChatChannelId));
  if (filters.externalChatPlatformId)
    params.append("external_chat_platform_id", String(filters.externalChatPlatformId));
  return params.toString();
};

export const fetchChatAnalyticsDashboard = async (
  tenantId: string | number,
  filters: ChatAnalyticsFilters = {}
): Promise<ChatAnalyticsDashboardResponse> => {
  const query = buildDashboardQuery(tenantId, filters);
  const response = await fetch(`${API_BASE_URL}/dashboard/chat-analytics/?${query}`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    await raiseForStatus(response, "Failed to load chat analytics dashboard");
  }
  const raw = await response.json();
  const bots = raw?.connected_bots;
  return {
    ...raw,
    connected_bots: Array.isArray(bots) ? bots.length : Number(bots) || 0,
    connected_bot_list: Array.isArray(bots) ? bots : [],
    external_chat_platforms: Array.isArray(raw?.external_chat_platforms) ? raw.external_chat_platforms : [],
    chat_counts_by_channels: raw?.chat_counts_by_channels || [],
    chat_counts_by_agents: raw?.chat_counts_by_agents || [],
    chat_counts_by_assigned_teams: raw?.chat_counts_by_assigned_teams || [],
  };
};

export const exportChatAnalyticsExcel = (tenantId: string | number, filters: ChatAnalyticsFilters = {}) =>
  requestExportByQuery("/dashboard/chat-analytics/export/", buildDashboardQuery(tenantId, filters), "chat analytics");

const asList = (data: any): any[] =>
  Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];

export const fetchSalesUserOptions = async (
  tenantId: number
): Promise<FilterOption[]> => {
  const data = await ChatService.getTenantUsers(tenantId);
  return asList(data).map((u: any) => ({
    value: u.user_id,
    label: `${u.user_first_name || ""} ${u.user_last_name || ""}`.trim() || u.email,
  }));
};

export const fetchAssignedTeamOptions = async (
  tenantId: number
): Promise<FilterOption[]> => {
  const data = await ChatService.getTeams(tenantId);
  return asList(data).map((t: any) => ({
    value: t.team_id,
    label: t.team_name || t.name,
  }));
};

export const fetchChatBotOptions = async (
  tenantId: number
): Promise<BotFilterOption[]> => {
  const data = await ChatService.getExternalChatTenantBot(tenantId);
  return asList(data).map((b: any) => ({
    value: b.external_chat_tenant_bot_id,
    label: b.bot_name,
    isInternal: isInternalPlatform(b.external_chat_platform?.external_chat_platform_display_name),
  }));
};

export const fetchChatChannelOptions = async (
  tenantId: number
): Promise<FilterOption[]> => {
  const data = await ChatService.getChannels(tenantId);
  return asList(data).map((c: any) => ({
    value: c.external_chat_channel_id,
    label: c.external_chat_channel_display_name,
  }));
};

export const fetchChatStatusOptions = async (
  tenantId: number
): Promise<FilterOption[]> => {
  const data = await ChatService.getEndUserStatusOptions(tenantId);
  return asList(data).map((s: any) => ({
    value: s.external_chat_end_user_status_id,
    label: s.external_chat_end_user_status_display_name,
  }));
};

export const fetchChatTypeOptions = async (): Promise<FilterOption[]> => {
  const response = await fetch(`${API_BASE_URL}/chat-type/`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    await raiseForStatus(response, "Failed to load chat types");
  }
  const data = await response.json();
  return asList(data).map((t: any) => ({
    value: t.chat_type_id,
    label: t.chat_type_display_name,
  }));
};
